import httpClient from "../http/http-client";
import { IBuildingType, ICountry, IEditLocation, ILocation, IPostDeactivateLocation, IPostLocation, IMeter, IMeterReading } from './location.model';

export default class LocationService {
  http;
  locations?: ILocation[];
  activeLocations?: ILocation[];
  countries?: ICountry[];

  static instance = new LocationService();

  static getInstance() {
    if (LocationService.instance === null) LocationService.instance = new LocationService();
    return this.instance;
  }

  constructor() {
    this.http = httpClient
  }

  async getAllLocations(forceUpdate: boolean = false): Promise<ILocation[]> {
    if (this.locations && !forceUpdate) {
      return Promise.resolve(this.locations);
    } else {
      return this.http.get('location')
        .then(response => {
          this.locations = response.data.rows;
          this.activeLocations = response.data.rows.filter((location: ILocation) => location.active)
          return response.data.rows;
        })
        .catch(err => {
          console.log(err);
          throw err;
        })
    }
  }

  async getActiveLocations(forceUpdate: boolean = false): Promise<ILocation[]> {
    if (this.activeLocations && !forceUpdate) {
      return Promise.resolve(this.activeLocations);
    } else {
      return this.http.get('location')
        .then(response => {
          this.locations = response.data.rows;
          this.activeLocations = response.data.rows.filter((location: ILocation) => location.active)
          return response.data.rows.filter((location: ILocation) => location.active);
        })
        .catch(err => {
          console.log(err);
          throw err;
        })
    }
  }

  async getAllLocationsWithPagination(active: boolean, searchQuery: string, page: number, perPage: number = 10, orderBy?: string, order?: 'asc' | 'desc'): Promise<{ count: number, rows: ILocation[] }> {
    return this.http.get(`location?active=${active}&nameIncludes=${searchQuery}&page=${page}&perPage=${perPage}${order && orderBy ? `&orderBy=${orderBy}&order=${order}` : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err)
        throw err;
      });
  }

  async getOneLocation(id: string): Promise<ILocation> {
    return this.http.get(`location/${id}`)
      .then(response => {
        return response.data
      });
  }

  async create(newLocation: IPostLocation): Promise<ILocation> {
    return this.http.post('location', newLocation)
      .then(response => {
        return response.data
      });
  }

  async update(id: string, newLocation: IEditLocation): Promise<ILocation> {
    return this.http.patch(`location/${id}`, newLocation)
      .then(response => {
        return response.data
      });
  }

  /**
   * Bulk-update only postcode + country across several locations.
   *
   * Deliberately NOT `update()` in a loop: PATCH `location/:id` expects a complete
   * `IEditLocation` and would blank every field not supplied. This hits a narrow endpoint that
   * writes just these two fields; the backend re-derives map coordinates from them.
   */
  async updateGeocodeDetails(
    locations: Array<{ id: string; postcode: string | null; countryId: string }>,
  ): Promise<ILocation[]> {
    return this.http.patch('location/geocode-details', { locations })
      .then(response => {
        // Addresses just changed, so both cached lists this service hands out are stale.
        this.locations = undefined;
        this.activeLocations = undefined;
        return response.data
      });
  }

  async remove(locationId: string): Promise<ILocation> {
    return this.http.delete(`location/${locationId}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async deactivate(locationId: string, details?: IPostDeactivateLocation): Promise<ILocation> {
    return this.http.patch(`location/${locationId}/deactivate`, details)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }


  async getAllBuildingType(): Promise<IBuildingType[]> {
    return this.http.get('building-type')
      .then(response => {
        return response.data
      });
  }


  async getAllCountries(): Promise<ICountry[]> {
    if (this.countries) {
      return Promise.resolve(this.countries)
    } else {
      return this.http.get('country')
        .then(response => {
          this.countries = response.data
          return response.data
        });
    }
  }

  async getMetersByLocationId(locationId: string, page: number = 0, perPage: number = 10, orderBy?: string, order?: 'asc' | 'desc'): Promise<{ count: number, rows: IMeter[] }> {
    return this.http.get(`meter/location/${locationId}?page=${page}&perPage=${perPage}${order && orderBy ? `&orderBy=${orderBy}&order=${order}` : ''}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getMeterReadings(meterId: string): Promise<IMeterReading[]> {
    return this.http.get(`meter-reading/meter/${meterId}`)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async createMeter(meterData: {
    name: string;
    locationId: string;
    conversionId: string;
  }): Promise<IMeter> {
    return this.http.post('meter', meterData)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async createMeterReading(readingData: {
    readingDate: string;
    meterReadingAmount: number | undefined;
    meterId: string;
    reportPeriodId?: string;
  }): Promise<IMeterReading> {
    return this.http.post('meter-reading', readingData)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async updateMeter(meterId: string, meterData: {
    name: string;
    conversionId: string;
  }): Promise<IMeter> {
    return this.http.patch(`meter/${meterId}`, meterData)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async updateMeterReading(readingId: string, readingData: {
    readingDate: string;
    meterReadingAmount: number | undefined;
  }): Promise<IMeterReading> {
    return this.http.patch(`meter-reading/${readingId}`, readingData)
      .then(response => {
        return response.data
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async deleteMeterReading(readingId: string): Promise<void> {
    return this.http.delete(`meter-reading/${readingId}`)
      .then(() => {
        return;
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async searchMeterByPostcode(postcode: string): Promise<any> {
    return this.http.post('meter/search-by-postcode', { postcode })
      .then(response => {
        console.log(response)
        return response.data;
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async getMeterDetailsByAddressId(addressId: string, locationId?: string, subsidiaryId?: string, fuelType?: string, customerConsent?: string, periodStartDate?: string, periodEndDate?: string): Promise<any> {
    return this.http.post('meter/meter-details-by-address-id', {
      addressId,
      locationId,
      subsidiaryId,
      fuelType: fuelType || 'elec',
      customerConsent: customerConsent || 'yes',
      periodStartDate,
      periodEndDate
    })
      .then(response => {
        return response.data;
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  async refreshMeterReadings(meterId: string, reportPeriodId?: string): Promise<any> {
    return this.http.post('meter/refresh-readings', { 
      meterId,
      reportPeriodId
    })
      .then(response => {
        return response.data;
      })
      .catch(err => {
        console.log(err);
        throw err;
      });
  }

  clearData() {
    this.locations = undefined;
    this.activeLocations = undefined;
    // countries is global reference data, identical for every organisation, so it
    // deliberately survives a client switch.
  }
}